/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import isNil from 'lodash/isNil';

import Idle from '../../idle/idle';
import Numeric from '../../numeric/numeric';
import Queue from '../../queue/queue';
import BaseViewerManagerController, {
  defaultScreenId,
  getContentTasks
} from './base-viewer-manager-controller';

// @ts-ignore Není to ESM.
import popupIdleHtml from './popup-idle.html';

/**
 * @import { IAnalyticsTracker } from '../../analytics'
 * @import { ViewerManagerController } from '.'
 * @import { Conditions, ContentInfo, LimitExpireCondition } from '../../content/info/content-info'
 * @import { ContentTaskCronFactory } from '../../content/task/cron/content-task-cron-factory'
 * @import { Logger } from '../../logger/logger'
 * @import { ContentControllerFactory } from '../../content/controller'
 */

/**
 * @private
 * @typedef {Record<string, *>} AppManager TODO #555
 * @typedef {Record<string, *>} ContentTaskCron TODO #555
 */

const defaultPopupTimeoutInSec = 5;

/**
 * @implements {ViewerManagerController}
 */
export default class UniversalController extends BaseViewerManagerController {
  /* eslint-disable max-statements */
  /**
   * @param {IAnalyticsTracker} analyticsTracker
   * @param {AppManager} appManager
   * @param {ContentControllerFactory} contentControllerFactory
   * @param {ContentInfo} contentInfo
   * @param {ContentTaskCronFactory} contentTaskCronFactory
   * @param {HTMLElement} el
   * @param {Logger} logger
   */
  constructor(
    analyticsTracker,
    appManager,
    contentControllerFactory,
    contentInfo,
    contentTaskCronFactory,
    el,
    logger
  ) {
    super(
      analyticsTracker,
      contentControllerFactory,
      contentInfo,
      contentTaskCronFactory,
      el
    );
    if (contentInfo.visualController.type === 'BasicController') {
      throw new Error(
        'UniversalController created with unexpected ContentInfo.'
      );
    }
    this.appManager = appManager;
    this.contentTaskCronFactory = contentTaskCronFactory;
    /** @type {ContentTaskCron | null} */
    this.contentTaskCron = null;
    this.logger = logger;
    /** @type {Idle | null} */
    this.idle = null;
    this.screenConditions = contentInfo.visualController.screenConditions;
    /** @type {Conditions | undefined} */
    this.display1ScreenConditions = this.screenConditions[defaultScreenId];

    // Pokud byla zadána startovací podmínka, tak se použije zvolený target.
    // Jinak se použije vybraný target.
    this.initTarget =
      contentInfo.visualController.screenConditions[defaultScreenId]?.[
        'condition.startApplication'
      ]?.action.toTarget ?? this.display1InitTarget;
    this.target = this.initTarget;
    this.changeContentQueue = new Queue();

    /** @type {ReturnType<typeof setTimeout> | undefined} */
    this.overlayTimeout = undefined;

    this.userActivity = this.userActivity.bind(this);
    this.userActivityOverlay = this.userActivityOverlay.bind(this);
    this.onIdle = this.onIdle.bind(this);

    /** @type {(() => void) | undefined} */
    this.hideOverlay = undefined;

    /** @type {ChildNode | null} */
    this.popupIdleHtmlEl = null;

    /** @type {HTMLElement | null} */
    this.popupIdleHtmlCounterEl = null;

    /** @type {Promise<boolean>} */
    this.hasIdleTime = this.appManager.hasIdleTime;

    this.hasConditionLimitExpire = !isNaN(
      getConditionLimitExpire(
        this.display1ScreenConditions?.['condition.limitExpire']
      )
    );
    // Vložení popupu s GUI pro odpočet, pokud
    if (this.hasConditionLimitExpire) {
      const parser = new DOMParser();
      const container = parser.parseFromString(popupIdleHtml, 'text/html');
      if (container.body.firstChild === null) {
        throw new Error('missing root element');
      }
      this.popupIdleHtmlEl = container.body.firstChild;

      const counter = container.getElementById('popup-idle-counter');
      if (counter === null) {
        throw new Error('missing counter element');
      }
      this.popupIdleHtmlCounterEl = counter;
    }
  }

  /**
   * @protected
   * @param {string | undefined} fromTarget
   * @param {string} toTarget
   * @returns {Promise<void>}
   */
  changeContent(fromTarget, toTarget) {
    return this.changeContentQueue.enqueue(async () => {
      // Pokud bylo podmíněno přepnutí na jiný target aktuálním targetem.
      // I '' se považuje za nezadání fromTargetu.
      if (fromTarget && this.target !== fromTarget) {
        return;
      }

      // Pokud již běží požadovaný target, tak není potřeba nic dělat.
      if (this.target === toTarget) {
        return;
      }

      await super.stop();
      await super.preload(
        getContentTasks(this.contentInfo.contentTasks, toTarget)
      );
      await super.start();

      this.target = toTarget;
    });
  }

  /**
   * Event listener použitý pro událost idle.
   * @returns {Promise<void>}
   */
  async onIdle() {
    try {
      const limitExpire =
        this.display1ScreenConditions?.['condition.limitExpire'];
      if (
        isNil(this.popupIdleHtmlCounterEl) ||
        isNil(this.popupIdleHtmlEl) ||
        !limitExpire
      ) {
        return;
      }

      // Zastavení časovače.
      this.idle?.stop();

      // hasIdleTime nevyhazuje výjimky.
      if (await this.hasIdleTime) {
        // Pokud platforma podporuje idle metody, tak je možné rovnou vyvolat akci.
        await this.changeContent(undefined, limitExpire.action.toTarget);

        // Pokud bylo vyplněno pravidlo pro stisk klávesy.
        if (this.display1ScreenConditions?.['condition.keyPress']) {
          this.registerEventListener(this.userActivity);
        }
        return;
      }

      // Platforma nepodporuje idle metody, tak je nutné
      // před přepnutím upozornit, že dojde k přepnutí.
      let popupTimeoutInSec = defaultPopupTimeoutInSec;
      try {
        const timeoutInSec = this.contentInfo.communicatorSettings.get(
          'player.light.idlePopup.timeoutInSec'
        );
        if (timeoutInSec !== undefined) {
          popupTimeoutInSec = Numeric.parseIndex(timeoutInSec);
        }
      } catch {
        // Pokud není nastavena validní hodnota, tak necháme výchozí.
      }

      const stop = this.setTimeoutPopupInSec(
        this.popupIdleHtmlCounterEl,
        popupTimeoutInSec
      );

      this.hideOverlay = () => {
        // Zastaví se vnitřní časovač pro zobrazení counteru.
        stop();
        this.hideOverlay = undefined;
        if (isNil(this.popupIdleHtmlEl)) {
          return;
        }
        this.el?.removeChild(this.popupIdleHtmlEl);
      };
      this.el?.appendChild(this.popupIdleHtmlEl);

      // Registrace události pro skrytí dialogu.
      this.registerEventListener(this.userActivityOverlay);

      this.overlayTimeout = setTimeout(async () => {
        if (!limitExpire) {
          // Nemůže nastat, protože se kontroluje na začátku metody.
          return;
        }
        this.hideOverlay?.();
        // Pokud platforma podporuje idle metody, tak je možné rovnou vyvolat akci.
        await this.changeContent(undefined, limitExpire.action.toTarget);

        // Pokud bylo vyplněno pravidlo pro stisk klávesy.
        if (this.display1ScreenConditions?.['condition.keyPress']) {
          this.registerEventListener(this.userActivity);
        }
      }, popupTimeoutInSec * 1000);
    } catch (err) {
      this.logger.error(err);
    }
  }

  /**
   * @param {HTMLElement} valueElem
   * @param {number} value Inicializační hodnota counteru v popupu.
   * @returns {() => void}
   */
  setTimeoutPopupInSec(valueElem, value) {
    if (!Number.isSafeInteger(value) || value < 0) {
      throw new Error('idle popup requires int');
    }
    valueElem.textContent = `${value}`;

    let currentValue = value;
    const intervalId = setInterval(() => {
      currentValue -= 1;
      valueElem.textContent = `${currentValue}`;
      if (currentValue <= 0) {
        clearInterval(intervalId);
      }
    }, 1000);
    return () => {
      clearInterval(intervalId);
    };
  }

  /**
   * @private
   */
  userActivityOverlay() {
    this.unregisterEventListener(this.userActivityOverlay);
    // Zrušení časovače pro skrytí dialogu s výzvou pro dotyk.
    clearTimeout(this.overlayTimeout);

    this.hideOverlay?.();
    const ms = getConditionLimitExpire(
      this.display1ScreenConditions?.['condition.limitExpire']
    );
    if (isNaN(ms)) {
      return;
    }

    this.idle?.start(ms);
  }

  /**
   * @private
   * @param {Event} e
   * @returns {Promise<void>}
   */
  async userActivity(e) {
    this.unregisterEventListener(this.userActivity);

    if (isNil(this.screenConditions)) {
      return;
    }

    const keyPress = this.display1ScreenConditions?.['condition.keyPress'];
    if (isNil(keyPress)) {
      return;
    }
    const ms = getConditionLimitExpire(
      this.display1ScreenConditions?.['condition.limitExpire']
    );
    if (isNaN(ms)) {
      return;
    }

    // Vstupy budou validní, protože nevadliní byly zahozeny v ContentInfoParser.
    const { fromTarget, toTarget } = keyPress.action;
    await this.changeContent(fromTarget, toTarget);
    this.idle?.start(ms);
  }

  /**
   * @private
   * @param {<K extends keyof DocumentEventMap>(this: Document, ev: DocumentEventMap[K]) => any} listener
   */
  registerEventListener(listener) {
    document.addEventListener('mousemove', listener);
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    document.addEventListener('keydown', listener);
  }

  /**
   * Nastaví novou sadu aktuálně přehrávaných obsahových úloh.
   * Nepřehrává se více úloh zároveň, ale mohou se střídat v čase,
   * což zajišťuje CRON, který se zároveň spustí.
   * @type {ViewerManagerController['start']}
   */
  async start() {
    const ms = getConditionLimitExpire(
      this.display1ScreenConditions?.['condition.limitExpire']
    );
    if (!isNaN(ms)) {
      if (!this.idle) {
        this.idle = new Idle(this.appManager, await this.hasIdleTime);
      }
      // Zapnout timer
      this.idle.start(ms);
      this.idle.on('idle', this.onIdle);
    }

    await super.preload(
      getContentTasks(this.contentInfo.contentTasks, this.initTarget)
    );
    await super.start();
  }

  /**
   * @private
   * @param {<K extends keyof DocumentEventMap>(this: Document, ev: DocumentEventMap[K]) => any} listener
   */
  unregisterEventListener(listener) {
    document.removeEventListener('mousemove', listener);
    document.removeEventListener('mousedown', listener);
    document.removeEventListener('touchstart', listener);
    document.removeEventListener('keydown', listener);
  }

  /**
   * Odebere sadu aktuálně přehrávaných obsahových úloh
   * a zastaví jejich plánování.
   * @type {ViewerManagerController['stop']}
   */
  async stop() {
    if (this.hasConditionLimitExpire) {
      this.idle?.off('idle', this.onIdle);
      this.idle?.stop();

      // Odregistrace událostí pro aktivitu uživatele.
      this.unregisterEventListener(this.userActivity);
      // Odregistrace událostí pro aktivitu uživatele v popupu.
      this.unregisterEventListener(this.userActivityOverlay);
      // Zrušení časovače pro skrytí dialogu s výzvou pro dotyk.
      clearTimeout(this.overlayTimeout);

      this.hideOverlay?.();

      // Zrušení časovače pro skrytí dialogu s výzvou pro dotyk.
      clearTimeout(this.overlayTimeout);

      // TODO: Neměly by se odřadit i jiné zdroje (kromě závislostí) vytvořené
      // v konstruktoru?
    }

    await super.stop();
  }
}

/**
 * @param {LimitExpireCondition | undefined} limitExpireCondition
 * @returns {number}
 */
function getConditionLimitExpire(limitExpireCondition) {
  const limitExpire = limitExpireCondition?.limitInSec;
  if (isNil(limitExpire)) {
    return NaN;
  }
  return limitExpire * 1000;
}
